/**
 * Utility functions and default schema for Mobile Web Styling
 */

export const DEFAULT_MOBILE_STYLES = {
  global: {
    banner: {
      bg_type: 'glass', // 'glass' | 'solid' | 'gradient'
      bg_color: '#0a0e17',
      bg_gradient: 'linear-gradient(180deg, #111827 0%, #0a0e17 100%)',
      custom_logo_url: '',
      custom_icon_url: '',
      logo_height: 34,
      logo_offset_x: 0,
      logo_offset_y: 0,
      title_color: '#ffffff',
      title_font_size: 14,
      title_letter_spacing: -0.2,
      subtitle_color: '#94a3b8',
      highlight_color: '#fbbf24',
      border_color: 'rgba(255, 255, 255, 0.08)',
      border_width: 1,
      blur: 12,
      opacity: 88,
      shadow: '0 4px 20px rgba(0,0,0,0.4)',
      glow: 'none', // 'none' | 'subtle' | 'primary' | 'gold' | 'neon'
      alignment: 'space-between' // 'left' | 'center' | 'space-between'
    },
    floating_menus: {
      bg_type: 'glass', // 'glass' | 'solid' | 'gradient'
      bg_color: '#111827',
      bg_gradient: 'linear-gradient(135deg, rgba(17,24,39,0.95), rgba(15,23,42,0.9))',
      opacity: 88,
      blur: 20,
      border_radius: 9999,
      border_color: 'rgba(255, 255, 255, 0.12)',
      border_width: 1,
      shadow: '0 16px 40px rgba(0, 0, 0, 0.65)',
      glow: '0 0 15px rgba(56, 189, 248, 0.2)',
      active_tab_color: '#38bdf8',
      active_tab_bg: 'rgba(56, 189, 248, 0.14)',
      inactive_tab_color: '#94a3b8',
      label_size: 10.5,
      label_weight: '600',
      alignment: 'center'
    },
    background: {
      type: 'pattern', // 'pattern' | 'gradient' | 'solid'
      color: '#0a0e17',
      gradient: 'linear-gradient(180deg, #0f172a 0%, #0a0e17 100%)',
      pattern: 'dots', // 'dots' | 'grid' | 'stripes' | 'carbon' | 'none'
      pattern_opacity: 25,
      glow: 'none'
    },
    badges: {
      bg_type: 'solid', // 'solid' | 'gradient' | 'glass'
      bg_color: '#10b981',
      bg_gradient: 'linear-gradient(135deg, #10b981, #059669)',
      text_color: '#ffffff',
      border_radius: 9999,
      border_color: 'rgba(255, 255, 255, 0.2)',
      border_width: 1,
      shadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
      glow: '0 0 10px rgba(16, 185, 129, 0.4)',
      font_size: 11,
      font_weight: '700',
      letter_spacing: 0.5,
      opacity: 100
    },
    content_panels: {
      bg_type: 'glass', // 'glass' | 'solid' | 'gradient'
      bg_color: '#111827',
      bg_gradient: 'linear-gradient(145deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.8))',
      border_radius: 16,
      border_color: 'rgba(255, 255, 255, 0.08)',
      border_width: 1,
      shadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
      glow: '0 0 20px rgba(56, 189, 248, 0.08)',
      opacity: 90,
      blur: 16
    },
    typography: {
      font_family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      heading_color: '#ffffff',
      body_color: '#94a3b8',
      accent_color: '#fbbf24',
      letter_spacing: -0.2,
      text_transform: 'none',
      alignment: 'left'
    }
  },
  pages: {}
};

/**
 * Safely parse mobile styles from string or object
 */
export function parseMobileStyles(raw) {
  if (!raw) return JSON.parse(JSON.stringify(DEFAULT_MOBILE_STYLES));
  if (typeof raw === 'object') {
    return {
      global: {
        banner: { ...DEFAULT_MOBILE_STYLES.global.banner, ...(raw.global?.banner || {}) },
        floating_menus: { ...DEFAULT_MOBILE_STYLES.global.floating_menus, ...(raw.global?.floating_menus || {}) },
        background: { ...DEFAULT_MOBILE_STYLES.global.background, ...(raw.global?.background || {}) },
        badges: { ...DEFAULT_MOBILE_STYLES.global.badges, ...(raw.global?.badges || {}) },
        content_panels: { ...DEFAULT_MOBILE_STYLES.global.content_panels, ...(raw.global?.content_panels || {}) },
        typography: { ...DEFAULT_MOBILE_STYLES.global.typography, ...(raw.global?.typography || {}) }
      },
      pages: raw.pages || {}
    };
  }
  try {
    const parsed = JSON.parse(raw);
    return parseMobileStyles(parsed);
  } catch (err) {
    console.error('Failed to parse mobile styles JSON:', err);
    return JSON.parse(JSON.stringify(DEFAULT_MOBILE_STYLES));
  }
}

/**
 * Resolves effective styling for a specific element on a specific page
 */
export function resolveElementStyle(styles, pageKey, elementKey) {
  const safeStyles = parseMobileStyles(styles);
  const globalStyle = safeStyles.global?.[elementKey] || DEFAULT_MOBILE_STYLES.global[elementKey];

  if (!pageKey) {
    return { style: { ...globalStyle }, isGlobal: true };
  }

  const pageData = safeStyles.pages?.[pageKey];
  const isOverridden = Boolean(pageData?.overrides?.[elementKey]);

  if (isOverridden && pageData?.[elementKey]) {
    return {
      style: { ...globalStyle, ...pageData[elementKey] },
      isGlobal: false
    };
  }

  return {
    style: { ...globalStyle },
    isGlobal: true
  };
}

/**
 * Generates SVG/CSS pattern background style
 */
export function getPatternBackground(pattern, patternOpacity = 25) {
  const op = Math.max(0, Math.min(100, patternOpacity)) / 100;
  switch (pattern) {
    case 'dots':
      return {
        backgroundImage: `radial-gradient(circle, rgba(255, 255, 255, ${0.2 * op}) 1.2px, transparent 1.2px)`,
        backgroundSize: '20px 20px'
      };
    case 'grid':
      return {
        backgroundImage: `
          linear-gradient(to right, rgba(255, 255, 255, ${0.08 * op}) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(255, 255, 255, ${0.08 * op}) 1px, transparent 1px)
        `,
        backgroundSize: '24px 24px'
      };
    case 'stripes':
      return {
        backgroundImage: `repeating-linear-gradient(45deg, rgba(255, 255, 255, ${0.04 * op}), rgba(255, 255, 255, ${0.04 * op}) 10px, transparent 10px, transparent 20px)`
      };
    case 'carbon':
      return {
        backgroundImage: `
          linear-gradient(45deg, rgba(0, 0, 0, 0.4) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.4) 75%, rgba(0, 0, 0, 0.4)),
          linear-gradient(45deg, rgba(0, 0, 0, 0.4) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.4) 75%, rgba(0, 0, 0, 0.4))
        `,
        backgroundSize: '16px 16px',
        backgroundPosition: '0 0, 8px 8px'
      };
    default:
      return {};
  }
}

/**
 * Appends a cache-busting query parameter (_cb=timestamp) to an asset URL.
 * If cacheBusterKey is falsy, or the URL is empty/data:/blob:, the original URL is returned unchanged.
 */
export function appendCacheBuster(url, cacheBusterKey) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (!cacheBusterKey) return url;

  try {
    const [base, hash] = url.split('#');
    const [pathPart, queryPart] = base.split('?');
    const params = new URLSearchParams(queryPart || '');
    params.set('_cb', String(cacheBusterKey));
    const newQuery = params.toString();
    return `${pathPart}?${newQuery}${hash ? `#${hash}` : ''}`;
  } catch (e) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_cb=${cacheBusterKey}`;
  }
}

/**
 * Builds CSS style object for canvas background
 */
export function buildBackgroundStyle(bgConfig, cacheBusterKey = null) {
  if (!bgConfig) return {};
  const base = {
    backgroundColor: bgConfig.color || '#0a0e17'
  };

  if (bgConfig.type === 'gradient' && bgConfig.gradient) {
    base.backgroundImage = bgConfig.gradient;
  } else if (bgConfig.type === 'pattern' && bgConfig.pattern && bgConfig.pattern !== 'none') {
    const patternStyle = getPatternBackground(bgConfig.pattern, bgConfig.pattern_opacity);
    Object.assign(base, patternStyle);
  } else if (bgConfig.type === 'image' && bgConfig.background_image_url) {
    const bgUrl = cacheBusterKey ? appendCacheBuster(bgConfig.background_image_url, cacheBusterKey) : bgConfig.background_image_url;
    base.backgroundImage = `url("${bgUrl}")`;
    base.backgroundSize = 'cover';
    base.backgroundPosition = 'center';
  }

  if (bgConfig.glow && bgConfig.glow !== 'none') {
    base.boxShadow = bgConfig.glow;
  }

  return base;
}

/**
 * Builds CSS style object for top banner
 */
export function buildBannerStyle(bannerConfig) {
  if (!bannerConfig) return {};
  const style = {
    borderBottom: `${bannerConfig.border_width ?? 1}px solid ${bannerConfig.border_color || 'rgba(255, 255, 255, 0.08)'}`,
    boxShadow: bannerConfig.shadow || 'none'
  };

  if (bannerConfig.bg_type === 'glass') {
    const alpha = (bannerConfig.opacity ?? 88) / 100;
    style.backgroundColor = hexToRgba(bannerConfig.bg_color || '#0a0e17', alpha);
    style.backdropFilter = `blur(${bannerConfig.blur ?? 12}px)`;
    style.WebkitBackdropFilter = `blur(${bannerConfig.blur ?? 12}px)`;
  } else if (bannerConfig.bg_type === 'gradient') {
    style.backgroundImage = bannerConfig.bg_gradient || 'linear-gradient(180deg, #111827, #0a0e17)';
  } else {
    style.backgroundColor = bannerConfig.bg_color || '#0a0e17';
  }

  return style;
}

/**
 * Builds CSS style object for bottom floating menu
 */
export function buildFloatingMenuStyle(menuConfig) {
  if (!menuConfig) return {};
  const style = {
    borderRadius: `${menuConfig.border_radius ?? 9999}px`,
    border: `${menuConfig.border_width ?? 1}px solid ${menuConfig.border_color || 'rgba(255, 255, 255, 0.12)'}`,
    boxShadow: `${menuConfig.shadow || '0 16px 40px rgba(0, 0, 0, 0.65)'}${menuConfig.glow && menuConfig.glow !== 'none' ? `, ${menuConfig.glow}` : ''}`
  };

  if (menuConfig.bg_type === 'glass') {
    const alpha = (menuConfig.opacity ?? 88) / 100;
    style.backgroundColor = hexToRgba(menuConfig.bg_color || '#111827', alpha);
    style.backdropFilter = `blur(${menuConfig.blur ?? 20}px)`;
    style.WebkitBackdropFilter = `blur(${menuConfig.blur ?? 20}px)`;
  } else if (menuConfig.bg_type === 'gradient') {
    style.backgroundImage = menuConfig.bg_gradient || 'linear-gradient(135deg, rgba(17,24,39,0.95), rgba(15,23,42,0.9))';
  } else {
    style.backgroundColor = menuConfig.bg_color || '#111827';
  }

  return style;
}

/**
 * Builds CSS style object for badges
 */
export function buildBadgeStyle(badgeConfig) {
  if (!badgeConfig) return {};
  const style = {
    color: badgeConfig.text_color || '#ffffff',
    borderRadius: `${badgeConfig.border_radius ?? 9999}px`,
    border: `${badgeConfig.border_width ?? 1}px solid ${badgeConfig.border_color || 'rgba(255, 255, 255, 0.2)'}`,
    fontSize: `${badgeConfig.font_size ?? 11}px`,
    fontWeight: badgeConfig.font_weight || '700',
    letterSpacing: `${badgeConfig.letter_spacing ?? 0.5}px`,
    boxShadow: `${badgeConfig.shadow || '0 2px 6px rgba(0, 0, 0, 0.3)'}${badgeConfig.glow && badgeConfig.glow !== 'none' ? `, ${badgeConfig.glow}` : ''}`
  };

  if (badgeConfig.bg_type === 'gradient') {
    style.backgroundImage = badgeConfig.bg_gradient || 'linear-gradient(135deg, #10b981, #059669)';
  } else if (badgeConfig.bg_type === 'glass') {
    const alpha = (badgeConfig.opacity ?? 80) / 100;
    style.backgroundColor = hexToRgba(badgeConfig.bg_color || '#10b981', alpha);
    style.backdropFilter = 'blur(8px)';
    style.WebkitBackdropFilter = 'blur(8px)';
  } else {
    style.backgroundColor = badgeConfig.bg_color || '#10b981';
  }

  return style;
}

/**
 * Builds CSS style object for content panels
 */
export function buildPanelStyle(panelConfig) {
  if (!panelConfig) return {};
  const style = {
    borderRadius: `${panelConfig.border_radius ?? 16}px`,
    border: `${panelConfig.border_width ?? 1}px solid ${panelConfig.border_color || 'rgba(255, 255, 255, 0.08)'}`,
    boxShadow: `${panelConfig.shadow || '0 10px 25px rgba(0, 0, 0, 0.5)'}${panelConfig.glow && panelConfig.glow !== 'none' ? `, ${panelConfig.glow}` : ''}`
  };

  if (panelConfig.bg_type === 'glass') {
    const alpha = (panelConfig.opacity ?? 90) / 100;
    style.backgroundColor = hexToRgba(panelConfig.bg_color || '#111827', alpha);
    style.backdropFilter = `blur(${panelConfig.blur ?? 16}px)`;
    style.WebkitBackdropFilter = `blur(${panelConfig.blur ?? 16}px)`;
  } else if (panelConfig.bg_type === 'gradient') {
    style.backgroundImage = panelConfig.bg_gradient || 'linear-gradient(145deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.8))';
  } else {
    style.backgroundColor = panelConfig.bg_color || '#111827';
  }

  return style;
}

/**
 * Helper to convert HEX color to rgba() string
 */
export function hexToRgba(hex, alpha = 1) {
  if (!hex || typeof hex !== 'string') return `rgba(17, 24, 39, ${alpha})`;
  if (hex.startsWith('rgba') || hex.startsWith('rgb')) return hex;
  let clean = hex.replace('#', '');
  if (clean.length === 3) {
    clean = clean.split('').map(c => c + c).join('');
  }
  if (clean.length !== 6) return `rgba(17, 24, 39, ${alpha})`;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
